/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid index.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import DataGridBase from './data-grid'
import { dataGridFeatures, type DataGridFeatures } from './engine/features'
import type { DataGridOptions, TableFeatures } from './types'
import { defineJQueryPlugin } from './util/index.js'

// The ENTRY, not the class, owns the default feature set: this one pairs the
// grid with the full dataGridFeatures, ./lite pairs the same class with
// dataGridLiteFeatures. Keeping the preset import out of data-grid.ts is what
// keeps the full set out of the lite bundle.
class DataGrid<TFeatures extends TableFeatures = DataGridFeatures> extends DataGridBase<TFeatures> {
  static get Default(): DataGridOptions {
    return { ...DataGridBase.Default, features: dataGridFeatures }
  }
}

defineJQueryPlugin(DataGrid)

export { DataGrid }
export { dataGridFeatures } from './engine/features'
export { dataGridLiteFeatures } from './engine/features-lite'
export { DEFAULT_LABELS, interpolate } from './labels'
export { evaluateColumnFilter, FILTER_OPERATORS, VALUELESS_OPERATORS } from './filters'
export { downloadCsv, exportCsv } from './util/csv'
export type { CsvDownloadOptions, CsvOptions } from './util/csv'
export type { DataGridExporter, DataGridExportOptions } from './ui/toolbar'
export type {
  Column,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  DataGridColumn,
  DataGridColumnFilterContext,
  DataGridColumnFilterFactory,
  DataGridColumnFilterValue,
  DataGridColumnMenuBuilder,
  DataGridColumnMenuContext,
  DataGridColumnSizingOptions,
  DataGridDataRequest,
  DataGridDataResponse,
  DataGridEditableOptions,
  DataGridEditCommitEvent,
  DataGridEditor,
  DataGridEditorContext,
  DataGridEditorFactory,
  DataGridEditorType,
  DataGridFeatures,
  DataGridFilterCondition,
  DataGridFilterOperator,
  DataGridFilterType,
  DataGridInfiniteScrollOptions,
  DataGridItem,
  DataGridLabels,
  DataGridLiteFeatures,
  DataGridMenuAction,
  DataGridOptions,
  DataGridPaginationInfoState,
  DataGridPaginationOptions,
  DataGridRowSelectionOptions,
  DataGridSlot,
  DataGridSlotContext,
  DataGridSlotFactory,
  DataGridSlots,
  DataGridSortingOptions,
  DataGridState,
  DataGridToolbarOptions,
  PaginationState,
  RowSelectionState,
  SortingState,
  ColumnVisibilityState,
  DataGridColumnPinning,
  DataGridRow,
  DataGridTable,
  DataGridTableState,
  Table,
  TableFeatures
} from './types'
